import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { QButton, QStack, useTheme } from '@captivateiq/quotient';
import { useCallback, useState } from 'react';
import {
  CartesianGrid,
  DefaultTooltipContent,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart as RechartsScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts/types/component/Tooltip';

import { useScatterPlot } from './hooks/useScatterPlot';
import { ScatterPlotContainer } from './ScatterPlotContainer.styles';

import { ScatterPlotProps } from '../../types';
import { getChartColors, displayLegend, formatLabel } from '../../utils/chartUtils';
import { ChartLegend } from '../BarChart/ChartLegend';

export const ScatterPlot = ({ targetLine, onClick, ...chartProps }: ScatterPlotProps) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
  const { data, formatValue, xAxisKeys, xAxisLabels, xAxisLabelSpacing, yAxisKeys, yAxisLabels, yAxisWidth } =
    useScatterPlot(chartProps);
  /**
   * Custom tooltip for scatter plot showing x and y coordinates using dynamic dataKeys
   */
  const handleTooltipHover = useCallback(
    ({ active, payload }: TooltipProps<ValueType, NameType>) => {
      if (active && payload?.length) {
        const dataPoint = payload[0].payload;
        const xValue = dataPoint[xAxisKeys[0]];
        const yValue = dataPoint[yAxisKeys[0]];

        // Get axis labels for better context
        const xAxisLabel = xAxisLabels && xAxisLabels.length > 0 ? xAxisLabels[0] : 'X Value';
        const yAxisLabel = yAxisLabels[0] || 'Y Value';

        const tooltipPayload = [
          {
            name: xAxisLabel,
            value: formatLabel(xValue),
            color: payload[0].color,
          },
          {
            name: yAxisLabel,
            value: formatValue(yValue),
            color: payload[0].color,
          },
        ];

        return <DefaultTooltipContent label="Data Point" payload={tooltipPayload} />;
      }
      return null;
    },
    [formatValue, xAxisKeys, xAxisLabels, yAxisKeys, yAxisLabels],
  );

  return (
    <ScatterPlotContainer data-testid="scatter-plot">
      {displayLegend(yAxisKeys) && (
        <QStack alignItems="flex-start" direction="row" spacing={1} sx={{ mb: 4 }}>
          <QButton
            color="primary"
            data-testid="view-legend-button"
            size="small"
            startIcon={faList}
            variant="text"
            onClick={() => setIsLegendModalOpen(true)}
          >
            View legend
          </QButton>
        </QStack>
      )}
      <ResponsiveContainer key={`scatter-container-${data?.length || 0}-${JSON.stringify(data?.slice(0, 3) || [])}`}>
        <RechartsScatterChart
          data={data}
          key={`scatter-chart-${data?.length || 0}`}
          margin={{ bottom: xAxisLabelSpacing(xAxisKeys[0]), right: 25, top: 10 }}
          onClick={onClick}
        >
          <CartesianGrid stroke={theme.palette.neutral.primary[100]} strokeDasharray="3 3" />
          <XAxis
            dataKey={xAxisKeys[0]}
            padding={{ right: 10 }}
            stroke={theme.palette.neutral.primary[700]}
            tick={{ fill: theme.palette.neutral.primary[700] }}
            tickFormatter={(value) => formatLabel(value)}
            tickLine={false}
            type="number"
          />
          <YAxis
            dataKey={yAxisKeys[0]}
            minTickGap={0.5}
            padding={{ top: 10 }}
            stroke={theme.palette.neutral.primary[700]}
            tick={{ fill: theme.palette.neutral.primary[700], textAnchor: 'end' }}
            tickFormatter={formatValue}
            tickLine={false}
            type="number"
            width={yAxisWidth}
          />
          <Tooltip content={handleTooltipHover} />

          <Scatter
            data={data}
            fill={chartColors[0]?.default || '#8884d8'}
            id="main-scatter-plot"
            isAnimationActive={false}
          />

          {/* Render target line */}
          {targetLine !== undefined && targetLine !== null && (
            <>
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.common.white}
                strokeLinecap="round"
                strokeWidth={4}
                y={targetLine}
              />
              <ReferenceLine
                ifOverflow="extendDomain"
                isFront
                stroke={theme.palette.denotive.positive[600]}
                strokeLinecap="round"
                strokeWidth={2}
                y={targetLine}
              />
            </>
          )}
        </RechartsScatterChart>
      </ResponsiveContainer>
      <ChartLegend
        chartColors={chartColors}
        labels={yAxisLabels}
        open={isLegendModalOpen}
        onClose={() => setIsLegendModalOpen(false)}
      />
    </ScatterPlotContainer>
  );
};
